/**
 * =========================================================
 * Social Media Hub - Premium Breadcrumbs
 * =========================================================
 *
 * Modern, sleek breadcrumbs with premium visual design
 * and polished user experience.
 */

// react-router-dom components
import { Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import { Breadcrumbs as MuiBreadcrumbs } from "@mui/material";
import Icon from "@mui/material/Icon";
import Box from "@mui/material/Box";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

function Breadcrumbs({ icon, title, route, light }) {
  const routes = route.slice(0, -1);

  return (
    <MDBox mr={{ xs: 0, xl: 8 }}>
      <MuiBreadcrumbs
        separator={
          <Icon
            sx={{
              fontSize: "0.75rem !important",
              color: light ? "rgba(255, 255, 255, 0.5)" : "#94a3b8",
            }}
          >
            chevron_right
          </Icon>
        }
        sx={{
          "& .MuiBreadcrumbs-separator": {
            mx: 0.5,
          },
          "& .MuiBreadcrumbs-li": {
            transition: "all 0.2s ease",
          },
        }}
      >
        <Link to="/">
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "8px",
              backgroundColor: light ? "rgba(255, 255, 255, 0.1)" : "rgba(99, 102, 241, 0.08)",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: light ? "rgba(255, 255, 255, 0.2)" : "rgba(99, 102, 241, 0.15)",
                transform: "scale(1.05)",
              },
            }}
          >
            <Icon
              sx={{
                fontSize: "1rem !important",
                color: light ? "white" : "#6366f1",
              }}
            >
              {icon}
            </Icon>
          </Box>
        </Link>
        {routes.map((el) => (
          <Link to={`/${el}`} key={el}>
            <MDTypography
              component="span"
              variant="button"
              fontWeight="500"
              textTransform="capitalize"
              sx={{
                color: light ? "rgba(255, 255, 255, 0.7)" : "#64748b",
                fontSize: "0.85rem",
                letterSpacing: "-0.01em",
                transition: "color 0.2s ease",
                "&:hover": {
                  color: light ? "white" : "#6366f1",
                },
              }}
            >
              {el}
            </MDTypography>
          </Link>
        ))}
        <MDTypography
          variant="button"
          fontWeight="600"
          textTransform="capitalize"
          sx={{
            color: light ? "white" : "#0f172a",
            fontSize: "0.85rem",
            letterSpacing: "-0.01em",
          }}
        >
          {title.replace("-", " ")}
        </MDTypography>
      </MuiBreadcrumbs>
      <MDTypography
        fontWeight="700"
        textTransform="capitalize"
        variant="h5"
        sx={{
          color: light ? "white" : "#0f172a",
          fontSize: { xs: "1.25rem", sm: "1.5rem" },
          letterSpacing: "-0.02em",
          mt: 0.5,
        }}
        noWrap
      >
        {title.replace("-", " ")}
      </MDTypography>
    </MDBox>
  );
}

// Setting default values for the props of Breadcrumbs
Breadcrumbs.defaultProps = {
  light: false,
};

// Typechecking props for the Breadcrumbs
Breadcrumbs.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  route: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  light: PropTypes.bool,
};

export default Breadcrumbs;
